/**
 * One word per state, and never a raw database string on a screen.
 *
 * Two tables, kept apart for the same reason the columns are: **a campaign's
 * status and a recipient's status are different questions.** A campaign can be
 * `completed` while a recipient in it is `expired`, and folding the two
 * vocabularies together is how a summary comes to disagree with its own list.
 */

import type { MessageKey } from '../i18n.ts';

export const STATUS_LABELS: Readonly<Record<string, MessageKey>> = {
  draft: 'status.draft',
  sending: 'status.sending',
  completed: 'status.completed',
  completed_with_errors: 'status.completedWithErrors',
  cancelled: 'status.cancelled',
  paused: 'status.paused',
  failed: 'status.failedCampaign',
};

export const RECIPIENT_LABELS: Readonly<Record<string, MessageKey>> = {
  pending: 'recipient.pending',
  sending: 'recipient.sending',
  accepted: 'recipient.accepted',
  delivered: 'recipient.delivered',
  not_delivered: 'recipient.notDelivered',
  expired: 'recipient.expired',
  failed: 'recipient.failed',
};

/**
 * Which of the three pill colours a campaign state gets.
 *
 * Three tones and seven states, so this is a judgement rather than a lookup:
 * **`cancelled` is neutral, not bad.** Somebody pressed Stop on purpose, and
 * colouring a deliberate act red teaches people to ignore red. `paused` is
 * amber because it is the one state that wants attention and can be fixed.
 */
export const STATUS_TONES: Readonly<Record<string, string>> = {
  draft: 'pill',
  sending: 'pill pill-warn',
  completed: 'pill pill-ok',
  completed_with_errors: 'pill pill-warn',
  cancelled: 'pill',
  paused: 'pill pill-warn',
  failed: 'pill pill-bad',
};

export function statusPill(status: string): string {
  return STATUS_TONES[status] ?? 'pill';
}
